import { useState, useEffect, useRef } from "react";
import { componentsAPI, seasonalDiscountsAPI, installationCostsAPI, pricingEngineAPI, companiesAPI } from "../lib/api";
// DEPRECATED: priceMatrixAPI wird nicht mehr verwendet
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Plus, Edit, Trash2, Sun, Battery, Zap, Box, Package, 
  Image as ImageIcon, Upload, Loader2, Shield, Copy, Globe, Eye, EyeOff, Cpu, Percent, Calendar,
  FileText, Download, Euro, Calculator, Building2, ArrowRight
} from "lucide-react";

const componentTypes = [
  { id: "module", name: "Module", icon: Sun },
  { id: "storage", name: "Speicher", icon: Battery },
  { id: "inverter", name: "Wechselrichter", icon: Zap },
  { id: "wallbox", name: "Wallbox", icon: Box },
  { id: "ems_ki", name: "EMS-KI", icon: Cpu },
  { id: "meter_cabinet", name: "Zählerschrank", icon: Package },
  { id: "grounding", name: "Erdung", icon: Package },
  { id: "scaffolding", name: "Gerüst", icon: Package },
  { id: "insurance", name: "Versicherung", icon: Shield },
  { id: "seasonal_discount", name: "Rabatt", icon: Percent },
  { id: "costs", name: "Kosten", icon: Calculator }
];

export default function ProductsPage() {
  const { user } = useAuth();
  const [components, setComponents] = useState([]);
  // DEPRECATED: priceMatrices wird nicht mehr verwendet
  // const [priceMatrices, setPriceMatrices] = useState([]);
  const [seasonalDiscounts, setSeasonalDiscounts] = useState([]);
  const [installationCosts, setInstallationCosts] = useState({ dc_costs: [], ac_costs: [] });
  const [savingCosts, setSavingCosts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("module");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [editingComponent, setEditingComponent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);  // Toggle to show/hide hidden components
  // DEPRECATED: Preismatrix-States werden nicht mehr verwendet
  // const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
  // const [editingMatrix, setEditingMatrix] = useState(null);
  // const [matrixFormData, setMatrixFormData] = useState({});
  // const [selectedMatrixTier, setSelectedMatrixTier] = useState("secure_plus");
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountFormData, setDiscountFormData] = useState({
    name: "",
    min_percent: 0,
    max_percent: 10,
    start_date: "",
    end_date: "",
    active: true
  });
  
  // Copy Costs Dialog State (Superadmin only)
  const [copyCostsDialogOpen, setCopyCostsDialogOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [companyCostsOverview, setCompanyCostsOverview] = useState([]);
  const [sourceCompanyId, setSourceCompanyId] = useState("");
  const [targetCompanyIds, setTargetCompanyIds] = useState([]);
  const [copyingCosts, setCopyingCosts] = useState(false);
  
  // Pricing Engine State
  const [pricingEngineStatus, setPricingEngineStatus] = useState(null);
  const [smokeTestResult, setSmokeTestResult] = useState(null);
  const [smokeTestLoading, setSmokeTestLoading] = useState(false);
  const [healthCheckResult, setHealthCheckResult] = useState(null);
  
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    type: "module",
    name: "",
    manufacturer: "",
    model: "",
    description: "",  // Freitext für Garantie und weitere Informationen
    price: 0,
    purchase_price: 0,  // Einkaufspreis für Margenberechnung
    additional_margin: 0,  // Zusätzliche Marge in EUR (wird auf VK addiert)
    capacity_kwh: 0,  // Basiskapazität in kWh (für Speicher)
    capacity_kwh_text: "",  // Text-Version für Eingabe mit Komma
    max_modules: null,
    sizes: [],
    sizesText: "",  // Text input for sizes
    insurance_available: false,
    backup_capable: false,  // For inverters - if backup/Notstrom is supported
    active: true,
    availability: "both",  // "standard", "secure_plus", or "both" - Produktverfügbarkeit
    watt: 450,  // Module power in Watt
    // Speichererweiterung Felder
    extension_name: "",
    extension_purchase_price: 0,
    extension_sales_price: 0,
    extension_capacity_kwh: 0,
    extension_capacity_kwh_text: "",
    max_extensions: 0
  });

  const isAdmin = user?.role === "superadmin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin";

  // Installation Costs Handlers
  const addDCCost = () => {
    setInstallationCosts(prev => ({
      ...prev,
      dc_costs: [...prev.dc_costs, { name: "", cost_per_module: 0, selling_price_per_module: 0 }]
    }));
  };

  const updateDCCost = (index, field, value) => {
    setInstallationCosts(prev => ({
      ...prev,
      dc_costs: prev.dc_costs.map((c, i) => 
        i === index ? { ...c, [field]: field === "name" ? value : (parseFloat(value.toString().replace(",", ".")) || 0) } : c
      )
    }));
  };

  const removeDCCost = (index) => {
    setInstallationCosts(prev => ({
      ...prev,
      dc_costs: prev.dc_costs.filter((_, i) => i !== index)
    }));
  };

  const addACCost = () => {
    setInstallationCosts(prev => ({
      ...prev,
      ac_costs: [...prev.ac_costs, { name: "", cost: 0, selling_price: 0 }]
    }));
  };

  const updateACCost = (index, field, value) => {
    setInstallationCosts(prev => ({
      ...prev,
      ac_costs: prev.ac_costs.map((c, i) => 
        i === index ? { ...c, [field]: field === "name" ? value : (parseFloat(value.toString().replace(",", ".")) || 0) } : c
      )
    }));
  };

  const removeACCost = (index) => {
    setInstallationCosts(prev => ({
      ...prev,
      ac_costs: prev.ac_costs.filter((_, i) => i !== index)
    }));
  };

  const saveInstallationCosts = async () => {
    setSavingCosts(true);
    try {
      await installationCostsAPI.update(installationCosts);
      toast.success("Installationskosten gespeichert");
    } catch (error) {
      console.error("Failed to save installation costs:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingCosts(false);
    }
  };

  // Copy Costs Functions (Superadmin only)
  const handleOpenCopyCostsDialog = async () => {
    try {
      const [companiesRes, overviewRes] = await Promise.all([
        companiesAPI.list(),
        installationCostsAPI.getAllCompanies()
      ]);
      setAllCompanies(companiesRes.data);
      setCompanyCostsOverview(overviewRes.data);
      setSourceCompanyId(user?.active_company_id || "");
      setTargetCompanyIds([]);
      setCopyCostsDialogOpen(true);
    } catch (error) {
      console.error("Failed to load companies:", error);
      toast.error("Fehler beim Laden der Firmendaten");
    }
  };

  const handleCopyCosts = async () => {
    if (!sourceCompanyId || targetCompanyIds.length === 0) {
      toast.error("Bitte wählen Sie eine Quelle und mindestens ein Ziel");
      return;
    }

    setCopyingCosts(true);
    try {
      const response = await installationCostsAPI.copy(sourceCompanyId, targetCompanyIds);
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh overview
        const overviewRes = await installationCostsAPI.getAllCompanies();
        setCompanyCostsOverview(overviewRes.data);
        setTargetCompanyIds([]);
      }
    } catch (error) {
      console.error("Failed to copy costs:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Kopieren");
    } finally {
      setCopyingCosts(false);
    }
  };

  const toggleTargetCompany = (companyId) => {
    setTargetCompanyIds(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  useEffect(() => {
    loadData();
  }, [showHidden]);

  const loadData = async () => {
    try {
      const [componentsRes, discountsRes, costsRes] = await Promise.all([
        componentsAPI.list(null, showHidden),  // Pass showHidden to include hidden components
        // DEPRECATED: priceMatrixAPI.list() - Preismatrix wird nicht mehr geladen
        seasonalDiscountsAPI.list(),
        installationCostsAPI.get().catch(() => ({ data: { dc_costs: [], ac_costs: [] } }))
      ]);
      setComponents(componentsRes.data);
      // DEPRECATED: setPriceMatrices(matricesRes.data);
      setSeasonalDiscounts(discountsRes.data || []);
      setInstallationCosts(costsRes.data || { dc_costs: [], ac_costs: [] });
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Produkte");
    } finally {
      setLoading(false);
    }
  };

  // Pricing Engine Functions
  const loadPricingEngineStatus = async () => {
    try {
      const [statusRes, healthRes] = await Promise.all([
        pricingEngineAPI.getStatus(),
        pricingEngineAPI.healthCheck()
      ]);
      setPricingEngineStatus(statusRes.data);
      setHealthCheckResult(healthRes.data);
    } catch (error) {
      console.error("Failed to load pricing engine status:", error);
    }
  };

  const runSmokeTest = async (tier = "secure_plus") => {
    setSmokeTestLoading(true);
    setSmokeTestResult(null);
    try {
      const result = await pricingEngineAPI.runSmokeTest(tier);
      setSmokeTestResult(result.data);
      toast.success(`Smoke Test ${result.data.status === "passed" ? "bestanden" : "fehlgeschlagen"}`);
    } catch (error) {
      console.error("Smoke test failed:", error);
      toast.error("Smoke Test fehlgeschlagen: " + (error.response?.data?.detail || error.message));
    } finally {
      setSmokeTestLoading(false);
    }
  };

  // Load pricing engine status on mount (for admins)
  useEffect(() => {
    if (isAdmin) {
      loadPricingEngineStatus();
    }
  }, [isAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Use the component's actual type when editing, otherwise use activeTab
      const componentType = editingComponent ? editingComponent.type : activeTab;
      
      // Parse sizes from text input
      const parsedSizes = formData.sizesText
        ? formData.sizesText.split(",").map(s => parseFloat(s.trim())).filter(n => !isNaN(n))
        : [];
      
      const data = {
        name: formData.name,
        manufacturer: formData.manufacturer,
        model: formData.model,
        description: formData.description,  // Freitext für Garantie und weitere Informationen
        price: formData.price,
        purchase_price: formData.purchase_price || 0,  // Einkaufspreis
        additional_margin: formData.additional_margin || 0,  // Zusätzliche Marge
        active: formData.active,
        availability: formData.availability || "both",  // Verfügbarkeit nach Anlagenklasse
        type: componentType,
      };
      
      // Add type-specific fields
      if (componentType === "module") {
        data.specifications = { watt: parseInt(formData.watt) || 450 };
        data.insurance_available = formData.insurance_available;
      } else if (componentType === "storage") {
        // Speicher mit Erweiterungskonfiguration
        data.capacity_kwh = formData.capacity_kwh || 10;  // Basiskapazität
        if (parsedSizes.length > 0) {
          data.sizes = parsedSizes;
        }
        // Speichererweiterung Felder
        data.extension_name = formData.extension_name || "";
        data.extension_purchase_price = formData.extension_purchase_price || 0;
        data.extension_sales_price = formData.extension_sales_price || 0;
        data.extension_capacity_kwh = formData.extension_capacity_kwh || 0;
        data.max_extensions = formData.max_extensions || 0;
      } else if (componentType === "inverter") {
        data.max_modules = formData.max_modules;
        data.backup_capable = formData.backup_capable;
      }

      if (editingComponent) {
        await componentsAPI.update(editingComponent.id, data);
        toast.success("Komponente aktualisiert");
      } else {
        await componentsAPI.create(data);
        toast.success("Komponente erstellt");
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Failed to save component:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diese Komponente wirklich löschen?")) return;
    
    try {
      await componentsAPI.delete(id);
      toast.success("Komponente gelöscht");
      loadData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleVisibility = async (component) => {
    try {
      const response = await componentsAPI.toggleVisibility(component.id);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      const errorMsg = error.response?.data?.detail || "Fehler beim Ändern der Sichtbarkeit";
      toast.error(errorMsg);
    }
  };

  const handleCopy = async (component) => {
    try {
      await componentsAPI.copy(component.id);
      toast.success(`"${component.name}" wurde für Ihre Firma kopiert. Sie können die Kopie jetzt bearbeiten.`);
      loadData();
    } catch (error) {
      console.error("Failed to copy:", error);
      const errorMsg = error.response?.data?.detail || "Fehler beim Kopieren";
      toast.error(errorMsg);
    }
  };

  const handleEdit = (component) => {
    setEditingComponent(component);
    setFormData({
      type: component.type,
      name: component.name,
      manufacturer: component.manufacturer || "",
      model: component.model || "",
      description: component.description || "",  // Freitext laden
      price: component.price,
      purchase_price: component.purchase_price || 0,  // Einkaufspreis laden
      additional_margin: component.additional_margin || 0,  // Zusätzliche Marge laden
      capacity_kwh: component.capacity_kwh || 0,  // Basiskapazität laden
      capacity_kwh_text: component.capacity_kwh ? String(component.capacity_kwh).replace('.', ',') : "",  // Als Text mit Komma
      max_modules: component.max_modules,
      sizes: component.sizes || [],
      sizesText: component.sizes?.join(", ") || "",  // Convert array to text
      insurance_available: component.insurance_available || false,
      backup_capable: component.backup_capable || false,
      active: component.active,
      availability: component.availability || "both",  // Verfügbarkeit laden
      watt: component.specifications?.watt || 450,
      // Speichererweiterung Felder
      extension_name: component.extension_name || "",
      extension_purchase_price: component.extension_purchase_price || 0,
      extension_sales_price: component.extension_sales_price || 0,
      extension_capacity_kwh: component.extension_capacity_kwh || 0,
      extension_capacity_kwh_text: component.extension_capacity_kwh ? String(component.extension_capacity_kwh).replace('.', ',') : "",
      max_extensions: component.max_extensions || 0
    });
    setDialogOpen(true);
  };

  const handleImageClick = (component) => {
    setSelectedComponent(component);
    setImageDialogOpen(true);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedComponent) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur PNG, JPEG oder WebP Dateien erlaubt");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Bild darf maximal 2MB groß sein");
      return;
    }

    setUploading(true);
    try {
      await componentsAPI.uploadImage(selectedComponent.id, file);
      toast.success("Bild erfolgreich hochgeladen");
      // Reload data and get the updated component
      const componentsRes = await componentsAPI.list(null, true);
      setComponents(componentsRes.data);
      // Find and update selected component with new data
      const updated = componentsRes.data.find(c => c.id === selectedComponent.id);
      if (updated) setSelectedComponent(updated);
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Fehler beim Hochladen des Bildes");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteImage = async () => {
    if (!selectedComponent) return;
    
    try {
      await componentsAPI.deleteImage(selectedComponent.id);
      toast.success("Bild erfolgreich entfernt");
      // Reload data and update selected component
      const componentsRes = await componentsAPI.list(null, true);
      setComponents(componentsRes.data);
      const updated = componentsRes.data.find(c => c.id === selectedComponent.id);
      if (updated) {
        setSelectedComponent(updated);
      } else {
        setSelectedComponent({ ...selectedComponent, image_url: null });
      }
    } catch (error) {
      console.error("Failed to delete image:", error);
      toast.error("Fehler beim Löschen des Bildes");
    }
  };

  // DEPRECATED: Matrix editing functions - Preismatrix wird nicht mehr verwendet
  // const handleEditMatrix = () => { ... };
  // const handleMatrixPriceChange = () => { ... };
  // const formatMatrixValue = () => { ... };
  // const parseMatrixValue = () => { ... };
  // const handleSaveMatrix = async () => { ... };

  // Seasonal Discount Functions
  const handleOpenDiscountDialog = (discount = null) => {
    if (discount) {
      setEditingDiscount(discount);
      setDiscountFormData({
        name: discount.name,
        min_percent: discount.min_percent,
        max_percent: discount.max_percent,
        start_date: discount.start_date,
        end_date: discount.end_date,
        active: discount.active
      });
    } else {
      setEditingDiscount(null);
      setDiscountFormData({
        name: "",
        min_percent: 0,
        max_percent: 10,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        active: true
      });
    }
    setDiscountDialogOpen(true);
  };

  const handleSaveDiscount = async () => {
    try {
      if (!discountFormData.name || !discountFormData.start_date || !discountFormData.end_date) {
        toast.error("Bitte füllen Sie alle Pflichtfelder aus");
        return;
      }

      if (editingDiscount) {
        await seasonalDiscountsAPI.update(editingDiscount.id, discountFormData);
        toast.success("Rabatt erfolgreich aktualisiert");
      } else {
        await seasonalDiscountsAPI.create(discountFormData);
        toast.success("Rabatt erfolgreich erstellt");
      }

      setDiscountDialogOpen(false);
      await loadData();
    } catch (error) {
      console.error("Failed to save discount:", error);
      toast.error("Fehler beim Speichern des Rabatts");
    }
  };

  const handleDeleteDiscount = async (discountId) => {
    if (!confirm("Möchten Sie diesen Rabatt wirklich löschen?")) return;
    
    try {
      await seasonalDiscountsAPI.delete(discountId);
      toast.success("Rabatt gelöscht");
      await loadData();
    } catch (error) {
      console.error("Failed to delete discount:", error);
      toast.error("Fehler beim Löschen des Rabatts");
    }
  };

  const isDiscountActive = (discount) => {
    const today = new Date().toISOString().split('T')[0];
    return discount.active && discount.start_date <= today && discount.end_date >= today;
  };

  const resetForm = () => {
    setEditingComponent(null);
    setFormData({
      type: activeTab,
      name: "",
      manufacturer: "",
      model: "",
      description: "",  // Freitext zurücksetzen
      price: 0,
      purchase_price: 0,  // Einkaufspreis zurücksetzen
      capacity_kwh: 0,  // Basiskapazität zurücksetzen
      capacity_kwh_text: "",  // Text zurücksetzen
      max_modules: null,
      sizes: [],
      sizesText: "",
      insurance_available: false,
      backup_capable: false,
      active: true,
      availability: "both",  // Verfügbarkeit zurücksetzen
      watt: 450,
      // Speichererweiterung zurücksetzen
      extension_name: "",
      extension_purchase_price: 0,
      extension_sales_price: 0,
      extension_capacity_kwh: 0,
      extension_capacity_kwh_text: "",
      max_extensions: 0
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const filteredComponents = components.filter(c => c.type === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Produkte</h1>
          <p className="text-muted-foreground">
            Verwalten Sie den Komponentenkatalog
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button 
              variant={showHidden ? "secondary" : "outline"} 
              onClick={() => setShowHidden(!showHidden)}
              title={showHidden ? "Versteckte ausblenden" : "Versteckte anzeigen"}
            >
              {showHidden ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
              {showHidden ? "Alle anzeigen" : "Versteckte zeigen"}
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-product-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neue Komponente
            </Button>
          </div>
        )}
      </div>

      {/* Pricing Engine Admin Panel - Only for Admins */}
      {isAdmin && pricingEngineStatus && (
        <Card className="mb-6 border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-['Manrope'] text-lg flex items-center gap-2">
                  <span className="text-blue-600">Pricing Engine</span>
                  <Badge variant={pricingEngineStatus?.mode === "CATALOG_COST" ? "default" : "secondary"}>
                    {pricingEngineStatus?.mode || "Lädt..."}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {pricingEngineStatus?.mode === "CATALOG_COST" 
                    ? "Kostenbasierte Preisberechnung aktiv (CATALOG_COST)" 
                    : pricingEngineStatus?.mode === "MATRIX"
                    ? "⚠️ Legacy-Preismatrix aktiv (MATRIX)" 
                    : "Status wird geladen..."}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => loadPricingEngineStatus()}
                >
                  Status aktualisieren
                </Button>
                <Button 
                  variant={smokeTestResult?.status === "passed" ? "default" : "secondary"}
                  size="sm"
                  onClick={() => runSmokeTest("secure_plus")}
                  disabled={smokeTestLoading}
                >
                  {smokeTestLoading ? "Läuft..." : "Smoke Test (Secure+)"}
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => runSmokeTest("standard")}
                  disabled={smokeTestLoading}
                >
                  {smokeTestLoading ? "Läuft..." : "Smoke Test (Standard)"}
                </Button>
              </div>
            </div>
          </CardHeader>
          
          {/* Health Check Results */}
          {healthCheckResult && (
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${healthCheckResult.status === "healthy" ? "bg-green-500" : "bg-yellow-500"}`}></span>
                  <span>Status: {healthCheckResult.status}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Komponenten: </span>
                  {healthCheckResult.checks?.collection_components?.count || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">Mit EK-Preis: </span>
                  {healthCheckResult.checks?.components_with_cost?.with_cost || 0}
                </div>
                <div>
                  <span className="text-muted-foreground">Preismatrizen: </span>
                  {healthCheckResult.checks?.collection_price_matrices?.count || 0}
                </div>
              </div>
              
              {/* Smoke Test Results */}
              {smokeTestResult && (
                <div className={`p-3 rounded-lg text-sm ${
                  smokeTestResult.status === "passed" ? "bg-green-100 border border-green-300" : 
                  smokeTestResult.status === "warning" ? "bg-yellow-100 border border-yellow-300" :
                  "bg-red-100 border border-red-300"
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">
                      Smoke Test: {smokeTestResult.status?.toUpperCase()} ({smokeTestResult.tier})
                    </span>
                    <span className="text-xs text-muted-foreground">{smokeTestResult.test_id}</span>
                  </div>
                  {smokeTestResult.calculation && (
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Gesamtkosten (EK): </span>
                        <span className="font-mono">{smokeTestResult.calculation?.costs?.total_cost?.toLocaleString('de-DE') || '0'} €</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Min. Listenpreis: </span>
                        <span className="font-mono">{smokeTestResult.calculation?.min_list_price?.toLocaleString('de-DE') || '0'} €</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Marge (40%+10%): </span>
                        <span className="font-mono">{smokeTestResult.calculation?.expected_margin_eur?.toLocaleString('de-DE') || '0'} €</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Show validation details for failed/warning tests */}
                  {smokeTestResult.validation && smokeTestResult.status !== "passed" && (
                    <div className="mt-2 text-xs">
                      {smokeTestResult.validation.missing_components?.length > 0 && (
                        <div className="text-red-600">
                          Fehlend: {smokeTestResult.validation.missing_components.join(", ")}
                        </div>
                      )}
                      {smokeTestResult.validation.components_without_cost?.length > 0 && (
                        <div className="text-yellow-600">
                          Ohne EK-Preis: {smokeTestResult.validation.components_without_cost.join("; ")}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-2 text-xs font-medium">
                    {smokeTestResult.recommendation}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2">
          {componentTypes.map((type) => (
            <TabsTrigger key={type.id} value={type.id} className="flex items-center gap-2">
              <type.icon className="h-4 w-4" />
              {type.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {componentTypes.map((type) => (
          <TabsContent key={type.id} value={type.id} className="mt-6">
            {/* Special handling for Seasonal Discount Tab */}
            {type.id === "seasonal_discount" ? (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-['Manrope']">Saisonale Rabatte</CardTitle>
                    <CardDescription>
                      Konfigurieren Sie zeitlich begrenzte Rabatte für Vertriebler
                    </CardDescription>
                  </div>
                  {isAdmin && (
                    <Button onClick={() => handleOpenDiscountDialog()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Neuer Rabatt
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {seasonalDiscounts.length > 0 ? (
                    <div className="space-y-4">
                      {seasonalDiscounts.map((discount) => {
                        const active = isDiscountActive(discount);
                        return (
                          <div 
                            key={discount.id} 
                            className={`border rounded-lg p-4 ${active ? 'border-green-500 bg-green-50' : 'border-muted'}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-lg">{discount.name}</h3>
                                  {discount.company_id === "global" && (
                                    <Badge variant="outline" className="text-xs">
                                      <Globe className="h-3 w-3 mr-1" />
                                      Global
                                    </Badge>
                                  )}
                                  {active ? (
                                    <Badge className="bg-green-500">Aktiv</Badge>
                                  ) : discount.active ? (
                                    <Badge variant="secondary">Geplant</Badge>
                                  ) : (
                                    <Badge variant="outline">Inaktiv</Badge>
                                  )}
                                </div>
                                <p className="text-muted-foreground flex items-center gap-2">
                                  <Percent className="h-4 w-4" />
                                  {discount.min_percent}% - {discount.max_percent}%
                                </p>
                                <p className="text-muted-foreground flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  {new Date(discount.start_date).toLocaleDateString('de-DE')} bis {new Date(discount.end_date).toLocaleDateString('de-DE')}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                {(isSuperAdmin || discount.company_id !== "global") && (
                                  <>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleOpenDiscountDialog(discount)}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="destructive" 
                                      size="sm"
                                      onClick={() => handleDeleteDiscount(discount.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Percent className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Keine saisonalen Rabatte konfiguriert</p>
                      <p className="text-sm">Erstellen Sie einen Rabatt, um ihn für Vertriebler freizugeben</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : type.id === "costs" ? (
              /* Installation Costs Tab */
              <div className="space-y-6">
                {/* Superadmin: Copy Costs Button */}
                {user?.role === "superadmin" && (
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">Kosten zwischen Firmen kopieren</p>
                            <p className="text-sm text-muted-foreground">
                              Übertragen Sie die Kostentabelle auf andere Firmen
                            </p>
                          </div>
                        </div>
                        <Button onClick={handleOpenCopyCostsDialog} variant="outline">
                          <Copy className="h-4 w-4 mr-2" />
                          Kosten kopieren
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* DC Costs - pro Modul */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-['Manrope']">DC-Kosten (pro Modul)</CardTitle>
                        <CardDescription>
                          Diese Kosten werden mit der Anzahl der Module multipliziert
                        </CardDescription>
                      </div>
                      <Button onClick={addDCCost} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Hinzufügen
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {installationCosts.dc_costs?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Kostenart</TableHead>
                            <TableHead className="text-right">EK pro Modul</TableHead>
                            <TableHead className="text-right">VK pro Modul</TableHead>
                            <TableHead className="text-right">Marge</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {installationCosts.dc_costs.map((cost, index) => {
                            const ek = cost.cost_per_module || 0;
                            const vk = cost.selling_price_per_module || 0;
                            const margin = vk > 0 ? ((vk - ek) / vk * 100).toFixed(0) : 0;
                            return (
                            <TableRow key={index}>
                              <TableCell>
                                <Input
                                  value={cost.name}
                                  onChange={(e) => updateDCCost(index, "name", e.target.value)}
                                  placeholder="z.B. Montage, Unterkonstruktion"
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={cost.cost_per_module}
                                    onChange={(e) => updateDCCost(index, "cost_per_module", e.target.value)}
                                    className="text-right w-24"
                                  />
                                  <span className="text-muted-foreground">€</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={cost.selling_price_per_module || ""}
                                    onChange={(e) => updateDCCost(index, "selling_price_per_module", e.target.value)}
                                    className="text-right w-24"
                                    placeholder="VK"
                                  />
                                  <span className="text-muted-foreground">€</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={`font-mono text-sm ${margin >= 50 ? 'text-green-600' : margin >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {margin}%
                                </span>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeDCCost(index)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Keine DC-Kosten definiert</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* AC Costs - Pauschalen */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-['Manrope']">AC-Kosten (Pauschalen)</CardTitle>
                        <CardDescription>
                          Diese Kosten werden einmalig pro Anlage berechnet
                        </CardDescription>
                      </div>
                      <Button onClick={addACCost} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Hinzufügen
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {installationCosts.ac_costs?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Kostenart</TableHead>
                            <TableHead className="text-right">EK Pauschale</TableHead>
                            <TableHead className="text-right">VK Pauschale</TableHead>
                            <TableHead className="text-right">Marge</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {installationCosts.ac_costs.map((cost, index) => {
                            const ek = cost.cost || 0;
                            const vk = cost.selling_price || 0;
                            const margin = vk > 0 ? ((vk - ek) / vk * 100).toFixed(0) : 0;
                            return (
                            <TableRow key={index}>
                              <TableCell>
                                <Input
                                  value={cost.name}
                                  onChange={(e) => updateACCost(index, "name", e.target.value)}
                                  placeholder="z.B. Zählerschrankmontage, WR-Montage"
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={cost.cost}
                                    onChange={(e) => updateACCost(index, "cost", e.target.value)}
                                    className="text-right w-24"
                                  />
                                  <span className="text-muted-foreground">€</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={cost.selling_price || ""}
                                    onChange={(e) => updateACCost(index, "selling_price", e.target.value)}
                                    className="text-right w-24"
                                    placeholder="VK"
                                  />
                                  <span className="text-muted-foreground">€</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={`font-mono text-sm ${margin >= 50 ? 'text-green-600' : margin >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {margin}%
                                </span>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeACCost(index)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Keine AC-Kosten definiert</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button onClick={saveInstallationCosts} disabled={savingCosts}>
                    {savingCosts ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Euro className="h-4 w-4 mr-2" />
                    )}
                    Kosten speichern
                  </Button>
                </div>
              </div>
            ) : filteredComponents.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredComponents.map((component) => {
                  const isGlobal = component.company_id === "global";
                  const isSuperadmin = user?.role === "superadmin";
                  const canEdit = isSuperadmin || !isGlobal;
                  
                  return (
                  <Card key={component.id} className={`overflow-hidden ${component.is_hidden ? 'opacity-60 border-dashed' : ''}`}>
                    {/* Image Section */}
                    <div 
                      className={`h-40 bg-muted flex items-center justify-center relative group ${canEdit ? 'cursor-pointer hover:bg-muted/80' : ''} transition-colors`}
                      onClick={() => canEdit && handleImageClick(component)}
                    >
                      {component.image_url ? (
                        <img 
                          src={component.image_url} 
                          alt={component.name}
                          className="h-full w-full object-contain p-4"
                        />
                      ) : (
                        <div className="text-center">
                          <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground/50" />
                          {canEdit && (
                            <p className="text-xs text-muted-foreground mt-2">Klicken zum Hochladen</p>
                          )}
                        </div>
                      )}
                      {canEdit && (
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Upload className="h-8 w-8 text-white" />
                        </div>
                      )}
                      {/* Hidden badge */}
                      {component.is_hidden && (
                        <div className="absolute top-2 right-2">
                          <Badge variant="secondary" className="bg-orange-500/80 text-white text-xs">
                            <EyeOff className="h-3 w-3 mr-1" />
                            Ausgeblendet
                          </Badge>
                        </div>
                      )}
                      {/* Global badge */}
                      {isGlobal && (
                        <div className="absolute top-2 left-2">
                          <Badge variant="outline" className="bg-background/80 text-xs">
                            <Globe className="h-3 w-3 mr-1" />
                            Global
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{component.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {component.manufacturer || "Kein Hersteller"}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant={component.active ? "default" : "secondary"}>
                            {component.active ? "Aktiv" : "Inaktiv"}
                          </Badge>
                          {component.availability && component.availability !== "both" && (
                            <Badge variant="outline" className={component.availability === "secure_plus" ? "border-blue-500 text-blue-600" : "border-orange-500 text-orange-600"}>
                              {component.availability === "secure_plus" ? "Secure+" : "Standard"}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 space-y-1 text-sm">
                        {type.id === "inverter" && component.max_modules && (
                          <p className="text-muted-foreground">
                            Max. {component.max_modules} Module
                          </p>
                        )}
                        {type.id === "inverter" && component.backup_capable && (
                          <div className="flex items-center gap-1 text-blue-600">
                            <Zap className="h-3 w-3" />
                            <span className="text-xs">Backup-fähig (Notstrom)</span>
                          </div>
                        )}
                        {type.id === "storage" && component.sizes?.length > 0 && (
                          <p className="text-muted-foreground">
                            {component.sizes.join(", ")} kWh
                          </p>
                        )}
                        {type.id === "module" && component.specifications?.watt && (
                          <p className="text-muted-foreground">
                            {component.specifications.watt} Watt pro Modul
                          </p>
                        )}
                        {type.id === "module" && component.insurance_available && (
                          <div className="flex items-center gap-1 text-green-600">
                            <Shield className="h-3 w-3" />
                            <span className="text-xs">Versicherung verfügbar</span>
                          </div>
                        )}
                        {component.datasheet_filename && (
                          <div className="flex items-center gap-1 text-blue-600">
                            <FileText className="h-3 w-3" />
                            <span className="text-xs">Datenblatt vorhanden</span>
                          </div>
                        )}
                        <p className="font-semibold text-lg text-accent">
                          {formatCurrency(component.price)}
                        </p>
                      </div>

                      {isAdmin && (
                        <div className="flex gap-2 mt-4 pt-4 border-t">
                          {canEdit ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleEdit(component)}
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Bearbeiten
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleVisibility(component)}
                                title={component.is_hidden ? "Einblenden" : "Ausblenden"}
                              >
                                {component.is_hidden ? (
                                  <Eye className="h-3 w-3" />
                                ) : (
                                  <EyeOff className="h-3 w-3" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(component.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleCopy(component)}
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Für meine Firma kopieren
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleVisibility(component)}
                                title={component.is_hidden ? "Einblenden" : "Ausblenden"}
                              >
                                {component.is_hidden ? (
                                  <Eye className="h-3 w-3" />
                                ) : (
                                  <EyeOff className="h-3 w-3" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="py-12">
                <CardContent className="text-center">
                  <type.icon className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">Keine {type.name} vorhanden</h3>
                  <p className="text-muted-foreground mb-4">
                    Fügen Sie Ihre erste Komponente hinzu
                  </p>
                  {isAdmin && (
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      {type.name} hinzufügen
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* DEPRECATED: Price Matrix - Preismatrix wurde deaktiviert
          Die Preisberechnung erfolgt jetzt ausschließlich über die kostenbasierte 
          Pricing-Engine basierend auf den Produktkatalog-Kosten.
          Formel: Verkaufspreis = Gesamtkosten / (1 - 0.40 - 0.10)
          (40% Firmenmarge + 10% Verkäuferprovision)
      */}

      {/* Component Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingComponent ? "Komponente bearbeiten" : "Neue Komponente"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Standard PV Modul 450W"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hersteller</Label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  placeholder="z.B. Winaico"
                />
              </div>
              <div className="space-y-2">
                <Label>Modell</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="z.B. WST-450MG"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Verkaufspreis (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div className="space-y-2">
              <Label>Einkaufspreis (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })}
                placeholder="Für Margenberechnung"
              />
              <p className="text-xs text-muted-foreground">
                Wird nur für die interne Margenberechnung verwendet
              </p>
            </div>

            <div className="space-y-2">
              <Label>Zusätzliche Marge (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.additional_margin}
                onChange={(e) => setFormData({ ...formData, additional_margin: parseFloat(e.target.value) || 0 })}
                placeholder="Wird auf VK addiert"
              />
              <p className="text-xs text-muted-foreground">
                Diese Marge wird zum Verkaufspreis addiert (z.B. für Handling, Service)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Zusatzinformationen (Freitext)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. Garantieleistungen, technische Details, besondere Eigenschaften..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Diese Informationen werden im PDF unter dem Artikel angezeigt
              </p>
            </div>

            {activeTab === "inverter" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Max. Module</Label>
                  <Input
                    type="number"
                    value={formData.max_modules || ""}
                    onChange={(e) => setFormData({ ...formData, max_modules: parseInt(e.target.value) || null })}
                    placeholder="z.B. 25"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-blue-600" />
                      Backup-fähig (Notstrom)
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Dieser Wechselrichter unterstützt Notstromversorgung
                    </p>
                  </div>
                  <Switch
                    checked={formData.backup_capable}
                    onCheckedChange={(checked) => setFormData({ ...formData, backup_capable: checked })}
                  />
                </div>
              </div>
            )}

            {(activeTab === "storage" || editingComponent?.type === "storage") && (
              <div className="space-y-4">
                {/* Basiskapazität */}
                <div className="space-y-2">
                  <Label>Basiskapazität (kWh)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.capacity_kwh_text !== undefined && formData.capacity_kwh_text !== "" ? formData.capacity_kwh_text : (formData.capacity_kwh ? String(formData.capacity_kwh).replace('.', ',') : "")}
                    onChange={(e) => {
                      const value = e.target.value;
                      const normalizedValue = value.replace(',', '.');
                      const numericValue = parseFloat(normalizedValue);
                      setFormData({ 
                        ...formData, 
                        capacity_kwh_text: value,
                        capacity_kwh: isNaN(numericValue) ? 0 : numericValue 
                      });
                    }}
                    placeholder="z.B. 10"
                  />
                  <p className="text-xs text-muted-foreground">
                    Grundkapazität des Speichers ohne Erweiterungen
                  </p>
                </div>

                {/* Speichererweiterung Sektion */}
                <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Battery className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">Speichererweiterung</h4>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Konfigurieren Sie hier das Erweiterungsmodul für diesen Speicher
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name des Erweiterungsmoduls</Label>
                      <Input
                        value={formData.extension_name}
                        onChange={(e) => setFormData({ ...formData, extension_name: e.target.value })}
                        placeholder="z.B. US3000C Modul"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max. Erweiterungen</Label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={formData.max_extensions}
                        onChange={(e) => setFormData({ ...formData, max_extensions: parseInt(e.target.value) || 0 })}
                        placeholder="z.B. 6"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>EK pro Modul (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.extension_purchase_price}
                        onChange={(e) => setFormData({ ...formData, extension_purchase_price: parseFloat(e.target.value) || 0 })}
                        placeholder="800"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>VK pro Modul (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.extension_sales_price}
                        onChange={(e) => setFormData({ ...formData, extension_sales_price: parseFloat(e.target.value) || 0 })}
                        placeholder="1500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Kapazität (kWh)</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={formData.extension_capacity_kwh_text !== undefined && formData.extension_capacity_kwh_text !== "" ? formData.extension_capacity_kwh_text : (formData.extension_capacity_kwh ? String(formData.extension_capacity_kwh).replace('.', ',') : "")}
                        onChange={(e) => {
                          const value = e.target.value;
                          const normalizedValue = value.replace(',', '.');
                          const numericValue = parseFloat(normalizedValue);
                          setFormData({ 
                            ...formData, 
                            extension_capacity_kwh_text: value,
                            extension_capacity_kwh: isNaN(numericValue) ? 0 : numericValue 
                          });
                        }}
                        placeholder="3,55"
                      />
                    </div>
                  </div>
                  
                  {formData.max_extensions === 0 && (
                    <p className="text-xs text-amber-600">
                      ⚠️ Setzen Sie "Max. Erweiterungen" auf einen Wert {">"} 0, um Erweiterungen zu aktivieren
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "module" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="watt">Modulleistung (Watt)</Label>
                  <Input
                    id="watt"
                    type="number"
                    value={formData.watt}
                    onChange={(e) => setFormData({ ...formData, watt: e.target.value })}
                    placeholder="z.B. 485"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leistung pro Modul in Watt für kWp-Berechnung
                  </p>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-green-600" />
                      Versicherung verfügbar
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      ERGO-Versicherung für diesen Modultyp anbieten
                    </p>
                  </div>
                  <Switch
                    checked={formData.insurance_available}
                    onCheckedChange={(checked) => setFormData({ ...formData, insurance_available: checked })}
                  />
                </div>
              </div>
            )}

            {/* Verfügbarkeit nach Anlagenklasse */}
            <div className="space-y-2">
              <Label>Verfügbarkeit</Label>
              <Select
                value={formData.availability}
                onValueChange={(value) => setFormData({ ...formData, availability: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Verfügbarkeit wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Standard & Secure Plus</SelectItem>
                  <SelectItem value="standard">Nur Standard</SelectItem>
                  <SelectItem value="secure_plus">Nur Secure Plus</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                In welcher Anlagenklasse ist dieses Produkt verfügbar?
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>

            {/* Datasheet Upload Section - only show when editing existing component */}
            {editingComponent && (
              <div className="space-y-2 pt-4 border-t">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Datenblatt (PDF)
                </Label>
                {editingComponent.datasheet_filename ? (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      <span className="text-sm">{editingComponent.datasheet_filename}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(componentsAPI.getDatasheetUrl(editingComponent.id), '_blank')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await componentsAPI.deleteDatasheet(editingComponent.id);
                            toast.success("Datenblatt entfernt");
                            setEditingComponent({ ...editingComponent, datasheet_filename: null });
                            loadData();
                          } catch (error) {
                            toast.error("Fehler beim Entfernen");
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            await componentsAPI.uploadDatasheet(editingComponent.id, file);
                            toast.success("Datenblatt hochgeladen");
                            setEditingComponent({ ...editingComponent, datasheet_filename: file.name });
                            loadData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
                          }
                        }
                      }}
                      className="text-sm"
                    />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Wird beim E-Mail-Versand des Angebots automatisch angehängt (max. 10 MB)
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit">
                {editingComponent ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              Produktbild - {selectedComponent?.name}
            </DialogTitle>
            <DialogDescription>
              Laden Sie ein Bild für diese Komponente hoch (PNG, JPEG, WebP, max. 2MB)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedComponent?.image_url ? (
              <div className="space-y-4">
                <div className="border rounded-lg p-4 bg-white">
                  <img
                    src={selectedComponent.image_url}
                    alt={selectedComponent.name}
                    className="max-h-48 mx-auto object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Bild ändern
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteImage}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2">
                  Klicken Sie hier oder ziehen Sie ein Bild
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPEG oder WebP, max. 2MB
                </p>
                {uploading && (
                  <Loader2 className="h-6 w-6 mx-auto mt-4 animate-spin text-primary" />
                )}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImageDialogOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DEPRECATED: Price Matrix Edit Dialog - Preismatrix wird nicht mehr verwendet */}

      {/* Seasonal Discount Edit Dialog */}
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingDiscount ? "Rabatt bearbeiten" : "Neuer saisonaler Rabatt"}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie einen zeitlich begrenzten Rabatt für Vertriebler.
              Der saisonale Rabatt beeinflusst nicht die Provision des Vertrieblers.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="discount-name">Rabattname (erscheint auf dem Angebot) *</Label>
              <Input
                id="discount-name"
                value={discountFormData.name}
                onChange={(e) => setDiscountFormData({...discountFormData, name: e.target.value})}
                placeholder="z.B. Winteraktion, Frühbucher-Rabatt"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min-percent">Minimum %</Label>
                <Input
                  id="min-percent"
                  type="number"
                  min="0"
                  max="50"
                  value={discountFormData.min_percent}
                  onChange={(e) => setDiscountFormData({...discountFormData, min_percent: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label htmlFor="max-percent">Maximum %</Label>
                <Input
                  id="max-percent"
                  type="number"
                  min="0"
                  max="50"
                  value={discountFormData.max_percent}
                  onChange={(e) => setDiscountFormData({...discountFormData, max_percent: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date">Startdatum *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={discountFormData.start_date}
                  onChange={(e) => setDiscountFormData({...discountFormData, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="end-date">Enddatum *</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={discountFormData.end_date}
                  onChange={(e) => setDiscountFormData({...discountFormData, end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="discount-active">Rabatt aktiv</Label>
              <Switch
                id="discount-active"
                checked={discountFormData.active}
                onCheckedChange={(checked) => setDiscountFormData({...discountFormData, active: checked})}
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveDiscount}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Costs Dialog (Superadmin only) */}
      <Dialog open={copyCostsDialogOpen} onOpenChange={setCopyCostsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Manrope']">
              <Copy className="h-5 w-5" />
              Installationskosten kopieren
            </DialogTitle>
            <DialogDescription>
              Kopieren Sie die Kostentabelle von einer Firma zu anderen Firmen.
              Bestehende Kosten werden überschrieben.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Source Company Selection */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Quelle (Kopieren von)</Label>
              <Select value={sourceCompanyId} onValueChange={setSourceCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Firma auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {companyCostsOverview
                    .filter(c => c.has_costs)
                    .map((company) => (
                      <SelectItem key={company.company_id} value={company.company_id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {company.company_name}
                          <Badge variant="outline" className="ml-2">
                            {company.dc_count} DC, {company.ac_count} AC
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRight className="h-6 w-6 text-muted-foreground" />
            </div>

            {/* Target Companies Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Ziele (Kopieren nach)</Label>
              <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                {companyCostsOverview
                  .filter(c => c.company_id !== sourceCompanyId)
                  .map((company) => (
                    <div 
                      key={company.company_id}
                      className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleTargetCompany(company.company_id)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={targetCompanyIds.includes(company.company_id)}
                          onCheckedChange={() => toggleTargetCompany(company.company_id)}
                        />
                        <div>
                          <p className="font-medium">{company.company_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {company.has_costs 
                              ? `${company.dc_count} DC, ${company.ac_count} AC Einträge vorhanden`
                              : "Keine Kosten konfiguriert"
                            }
                          </p>
                        </div>
                      </div>
                      {company.has_costs ? (
                        <Badge variant="secondary">Überschreiben</Badge>
                      ) : (
                        <Badge variant="outline">Neu anlegen</Badge>
                      )}
                    </div>
                  ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {targetCompanyIds.length} Firma(en) ausgewählt
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyCostsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleCopyCosts} 
              disabled={copyingCosts || !sourceCompanyId || targetCompanyIds.length === 0}
            >
              {copyingCosts ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kopiere...
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Zu {targetCompanyIds.length} Firma(en) kopieren
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
